// File: src/components/admin/promos/PromoCodeEditor.js
// Per-promo-code editor card. One card == one promo code. Mirrors PlanEditor.js.
//
// Editable: description, maxUses, expiresAt, status (active/archived).
// Read-only: redemption stats (uses / maxUses), durationDays, planId, code.
// Actions: Save (PUT) + Archive (POST .../archive).

import React, { useEffect, useState } from 'react';
import {
    Card, CardContent, CardActions, Stack, TextField, Button,
    Typography, Chip, MenuItem, CircularProgress, Alert,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArchiveIcon from '@mui/icons-material/Archive';
import { mainApi } from '../../../services/mainApi';

const STATUS_OPTIONS = ['active', 'archived'];

// ms epoch → value for <input type="datetime-local"> (local time, no seconds).
function epochToLocalInput(ms) {
    if (ms == null || ms === '') return '';
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function normaliseDraft(promo) {
    return {
        description: promo.description || '',
        // Blank string in the input == unlimited (null).
        maxUses: promo.maxUses == null ? '' : Number(promo.maxUses),
        expiresAt: epochToLocalInput(promo.expiresAt),
        status: promo.status || 'active',
    };
}

export default function PromoCodeEditor({ promo, onPromoChanged, planNameById = {} }) {
    const [draft, setDraft] = useState(() => normaliseDraft(promo));
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        setDraft(normaliseDraft(promo));
        setDirty(false);
        setError(null);
    }, [promo]);

    function patch(updates) {
        setDraft(prev => ({ ...prev, ...updates }));
        setDirty(true);
    }

    async function handleSave() {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const updates = {
                description: draft.description,
                status: draft.status,
                // Blank maxUses == unlimited → null so the BE clears any cap.
                maxUses: draft.maxUses === '' ? null : Number(draft.maxUses),
                // datetime-local string → ms epoch; blank == no expiry (null).
                expiresAt: draft.expiresAt ? new Date(draft.expiresAt).getTime() : null,
            };
            const updated = await mainApi.updatePromoCode(promo.code, updates);
            setSaving(false);
            setSuccess('Saved.');
            setDirty(false);
            if (onPromoChanged) onPromoChanged(updated);
        } catch (e) {
            setError(e?.response?.data?.error || e.message);
            setSaving(false);
        }
    }

    async function handleArchive() {
        try {
            const updated = await mainApi.archivePromoCode(promo.code);
            setSuccess('Promo code archived.');
            if (onPromoChanged) onPromoChanged(updated);
        } catch (e) {
            setError(e?.response?.data?.error || e.message);
        }
    }

    const archived = promo.status === 'archived';
    const maxLabel = promo.maxUses == null ? '∞' : promo.maxUses;
    // Friendly plan label when resolvable; otherwise fall back to the raw id so
    // promos referencing a since-removed plan still render without throwing.
    const planLabel = (promo.planId && planNameById[promo.planId]) || promo.planId;

    return (
        <Card
            variant="outlined"
            data-testid={`promo-editor-${promo.code}`}
            sx={{ opacity: archived ? 0.85 : 1 }}
        >
            <CardContent>
                {/* Header */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>
                        {promo.code}
                    </Typography>
                    <Chip
                        size="small"
                        color={archived ? 'warning' : 'success'}
                        label={archived ? 'Archived' : 'Active'}
                        data-testid={`promo-status-chip-${promo.code}`}
                    />
                </Stack>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}
                        data-testid={`promo-error-${promo.code}`}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}
                        data-testid={`promo-success-${promo.code}`}>
                        {success}
                    </Alert>
                )}

                {/* Read-only redemption stats */}
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${promo.uses ?? 0} / ${maxLabel} redeemed`}
                        data-testid={`promo-stats-${promo.code}`}
                    />
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${promo.durationDays} day${promo.durationDays === 1 ? '' : 's'}`}
                    />
                    {promo.planId && (
                        <Chip size="small" variant="outlined" label={`Plan: ${planLabel}`}
                            data-testid={`promo-plan-chip-${promo.code}`} />
                    )}
                    <Chip
                        size="small"
                        variant="outlined"
                        label={promo.expiresAt ? `Expires ${new Date(promo.expiresAt).toLocaleString()}` : 'No expiry'}
                    />
                </Stack>

                {/* Editable fields */}
                <Stack spacing={2}>
                    <TextField
                        label="Description"
                        size="small"
                        fullWidth
                        value={draft.description}
                        onChange={(e) => patch({ description: e.target.value })}
                        inputProps={{ 'data-testid': `promo-description-${promo.code}` }}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="Max uses"
                            type="number"
                            size="small"
                            value={draft.maxUses}
                            onChange={(e) => patch({ maxUses: e.target.value })}
                            sx={{ width: 160 }}
                            helperText="Blank = unlimited"
                            inputProps={{ 'data-testid': `promo-maxUses-${promo.code}`, min: 0 }}
                        />
                        <TextField
                            label="Expires at"
                            type="datetime-local"
                            size="small"
                            value={draft.expiresAt}
                            onChange={(e) => patch({ expiresAt: e.target.value })}
                            sx={{ width: 240 }}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ 'data-testid': `promo-expiresAt-${promo.code}` }}
                        />
                        <TextField
                            select
                            label="Status"
                            size="small"
                            value={draft.status}
                            onChange={(e) => patch({ status: e.target.value })}
                            sx={{ width: 140 }}
                            inputProps={{ 'data-testid': `promo-status-${promo.code}` }}
                        >
                            {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </TextField>
                    </Stack>
                </Stack>
            </CardContent>

            <CardActions sx={{ p: 2, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Button
                    size="small"
                    startIcon={<ArchiveIcon />}
                    onClick={handleArchive}
                    color="warning"
                    disabled={archived}
                    data-testid={`promo-archive-btn-${promo.code}`}
                >
                    Archive
                </Button>
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={!dirty || saving}
                    data-testid={`save-promo-${promo.code}`}
                >
                    {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </CardActions>
        </Card>
    );
}
